import { useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from 'react-i18next';
import { tenantSampleCreateSchema, type TenantSampleCreateFormValues } from '../schema/tenantSwitcherSchema';

interface SampleFormDialogProps {
  currentTenant: string;
  isPending: boolean;
  serverError: string | null;
  onSubmit: (values: TenantSampleCreateFormValues) => void;
  onClose: () => void;
}

const CATEGORY_OPTIONS = ['', 'CONTRACT', 'NOTE', 'MEMO'] as const;

export function SampleFormDialog({
  currentTenant,
  isPending,
  serverError,
  onSubmit,
  onClose,
}: SampleFormDialogProps) {
  const { t } = useTranslation();
  const firstInputRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TenantSampleCreateFormValues>({
    resolver: zodResolver(tenantSampleCreateSchema),
    defaultValues: {
      label: '',
      category: '',
      secret: '',
      note: '',
    },
  });

  // 포커스 트랩 — ESC 닫기
  useEffect(() => {
    firstInputRef.current?.focus();

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
        return;
      }
      // 포커스 트랩: Tab/Shift+Tab 을 dialog 내부로 제한
      if (e.key === 'Tab' && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'input, select, textarea, button:not([disabled])'
        );
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey) {
          if (document.activeElement === first) {
            e.preventDefault();
            last?.focus();
          }
        } else {
          if (document.activeElement === last) {
            e.preventDefault();
            first?.focus();
          }
        }
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) onClose();
    },
    [onClose]
  );

  const categoryLabel = (cat: string) => {
    if (!cat) return t('tenantSwitcher.category.empty');
    return t(`tenantSwitcher.category.${cat}`, { defaultValue: cat });
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="sample-form-title"
      aria-describedby="sample-form-desc"
      onClick={handleBackdropClick}
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1000,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'var(--color-overlay-backdrop)',
      }}
    >
      <div
        ref={dialogRef}
        style={{
          background: 'var(--color-surface-background)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--spacing-6)',
          width: 480,
          maxWidth: '95vw',
          boxShadow: 'var(--shadow-lg)',
          position: 'relative',
        }}
      >
        {/* 제목 */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: 'var(--spacing-4)',
          }}
        >
          <div>
            <h2
              id="sample-form-title"
              style={{
                fontSize: 'var(--font-size-lg)',
                fontWeight: 700,
                margin: 0,
                color: 'var(--color-text-primary)',
              }}
            >
              {t('tenantSwitcher.form.title')}
            </h2>
            <p
              id="sample-form-desc"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-muted)',
                margin: 'var(--spacing-1) 0 0',
              }}
            >
              {t('tenantSwitcher.form.subtitle', { tenant: currentTenant })}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('tenantSwitcher.form.button.cancel')}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontSize: 'var(--font-size-xl)',
              color: 'var(--color-text-muted)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            ✕
          </button>
        </div>

        {/* 서버 오류 */}
        {serverError && (
          <div
            role="alert"
            aria-live="assertive"
            style={{
              padding: 'var(--spacing-3)',
              background: 'color-mix(in srgb, var(--color-danger) 10%, transparent)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-danger)',
              marginBottom: 'var(--spacing-4)',
              border: '1px solid color-mix(in srgb, var(--color-danger) 30%, transparent)',
            }}
          >
            {serverError}
          </div>
        )}

        {/* 폼 */}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          {/* 라벨 */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="sample-label"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('tenantSwitcher.form.label')}{' '}
              <span aria-hidden="true" style={{ color: 'var(--color-danger)' }}>
                *
              </span>
            </label>
            <input
              id="sample-label"
              type="text"
              {...register('label')}
              ref={(el) => {
                register('label').ref(el);
                (firstInputRef as React.MutableRefObject<HTMLInputElement | null>).current = el;
              }}
              placeholder={t('tenantSwitcher.form.label.placeholder')}
              aria-required="true"
              aria-invalid={errors.label ? 'true' : 'false'}
              aria-describedby={errors.label ? 'sample-label-error' : undefined}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${errors.label ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
              }}
            />
            {errors.label && (
              <p
                id="sample-label-error"
                role="alert"
                style={{
                  color: 'var(--color-danger)',
                  fontSize: 'var(--font-size-xs)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {errors.label.message}
              </p>
            )}
          </div>

          {/* 분류 */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="sample-category"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('tenantSwitcher.form.category')}
            </label>
            <select
              id="sample-category"
              {...register('category')}
              aria-invalid={errors.category ? 'true' : 'false'}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
              }}
            >
              {CATEGORY_OPTIONS.map((opt) => (
                <option key={opt} value={opt}>
                  {categoryLabel(opt)}
                </option>
              ))}
            </select>
          </div>

          {/* 민감값 */}
          <div style={{ marginBottom: 'var(--spacing-4)' }}>
            <label
              htmlFor="sample-secret"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('tenantSwitcher.form.secret')}{' '}
              <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)' }}>
                🔒
              </span>
            </label>
            <input
              id="sample-secret"
              type="text"
              {...register('secret')}
              placeholder={t('tenantSwitcher.form.secret.placeholder')}
              aria-invalid={errors.secret ? 'true' : 'false'}
              aria-describedby="sample-secret-hint"
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: `1px solid ${errors.secret ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
              }}
            />
            <p
              id="sample-secret-hint"
              style={{
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-info)',
                marginTop: 'var(--spacing-1)',
              }}
            >
              ⓘ {t('tenantSwitcher.form.secret.hint')}
            </p>
            {errors.secret && (
              <p
                role="alert"
                style={{
                  color: 'var(--color-danger)',
                  fontSize: 'var(--font-size-xs)',
                  marginTop: 'var(--spacing-1)',
                }}
              >
                {errors.secret.message}
              </p>
            )}
          </div>

          {/* 메모 */}
          <div style={{ marginBottom: 'var(--spacing-6)' }}>
            <label
              htmlFor="sample-note"
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                color: 'var(--color-text-secondary)',
                marginBottom: 'var(--spacing-1)',
              }}
            >
              {t('tenantSwitcher.form.note')}
            </label>
            <input
              id="sample-note"
              type="text"
              {...register('note')}
              placeholder={t('tenantSwitcher.form.note.placeholder')}
              aria-invalid={errors.note ? 'true' : 'false'}
              style={{
                width: '100%',
                boxSizing: 'border-box',
                padding: 'var(--spacing-2) var(--spacing-3)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontFamily: 'var(--font-family-sans)',
                background: 'var(--color-surface-elevated)',
                color: 'var(--color-text-primary)',
              }}
            />
          </div>

          {/* 버튼 영역 */}
          <div
            style={{
              display: 'flex',
              gap: 'var(--spacing-2)',
              justifyContent: 'flex-end',
            }}
          >
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-surface-elevated)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                color: 'var(--color-text-secondary)',
              }}
            >
              {t('tenantSwitcher.form.button.cancel')}
            </button>
            <button
              type="submit"
              disabled={isPending}
              style={{
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: isPending ? 'var(--color-surface-muted)' : 'var(--color-brand-primary)',
                color: isPending ? 'var(--color-text-muted)' : 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: isPending ? 'not-allowed' : 'pointer',
                fontFamily: 'var(--font-family-sans)',
                opacity: isPending ? 0.7 : 1,
                minWidth: 72,
              }}
            >
              {isPending ? '●●●' : t('tenantSwitcher.form.button.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
